import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const _wakeBackend = () => {
  const api = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  axios.get(`${api}/health`, { timeout: 10000 }).catch(() => {});
};
_wakeBackend();

// ─── Palette ───────────────────────────────────────────────
const C = {
  navy    : '#003D4F',
  navyMid : '#005060',
  teal    : '#006D77',
  tealBr  : '#00A7B5',
  pool    : '#00B4C8',
  gold    : '#E8C547',
  goldDk  : '#C9A420',
  cream   : '#F7F0E5',
  sand    : '#EDE8DC',
  white   : '#FFFFFF',
  grey    : '#4A6470',
  text    : '#0F2D4A',
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body{font-family:'Outfit',sans-serif;background:${C.cream};color:${C.text};-webkit-font-smoothing:antialiased}

  @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
  @keyframes floatUp{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
  @keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(6px)}}
  @keyframes orbDriftA{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(24px,-18px) scale(1.08)}}
  @keyframes orbDriftB{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-20px,22px) scale(1.05)}}
  @keyframes orbDriftC{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(16px,16px) scale(1.1)}}

  .orb{position:absolute;border-radius:50%;pointer-events:none;filter:blur(2px)}
  .orb-a{animation:orbDriftA 14s ease-in-out infinite}
  .orb-b{animation:orbDriftB 17s ease-in-out infinite}
  .orb-c{animation:orbDriftC 20s ease-in-out infinite}

  .au{animation:fadeUp .65s cubic-bezier(.22,1,.36,1) both}
  .d1{animation-delay:.06s}.d2{animation-delay:.14s}.d3{animation-delay:.22s}
  .d4{animation-delay:.32s}.d5{animation-delay:.44s}

  .btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;
    padding:13px 28px;border-radius:100px;font-size:.93rem;font-weight:700;
    font-family:'Outfit',sans-serif;cursor:pointer;border:none;
    transition:all .2s;text-decoration:none;white-space:nowrap;letter-spacing:-.01em}
  .btn:active{transform:scale(.97)!important}
  .btn-gold{background:${C.gold};color:${C.navy};box-shadow:0 4px 18px rgba(232,197,71,.38)}
  .btn-gold:hover{background:${C.goldDk};transform:translateY(-2px);box-shadow:0 8px 28px rgba(232,197,71,.48)}
  .btn-outline{background:transparent;color:${C.cream};border:1.5px solid rgba(255,248,237,.35)}
  .btn-outline:hover{background:rgba(255,255,255,.1);transform:translateY(-2px)}
  .btn-teal{background:${C.teal};color:#fff;box-shadow:0 4px 16px rgba(0,109,119,.25)}
  .btn-teal:hover{background:${C.tealBr};transform:translateY(-2px)}
  .btn-ghost-dk{background:transparent;color:${C.teal};border:1.5px solid ${C.teal}}
  .btn-ghost-dk:hover{background:${C.teal};color:#fff;transform:translateY(-2px)}
  .btn-white{background:#fff;color:${C.navy};box-shadow:0 4px 18px rgba(0,30,50,.18)}
  .btn-white:hover{transform:translateY(-2px);box-shadow:0 10px 32px rgba(0,30,50,.28)}

  .card{background:#fff;border-radius:18px;border:1px solid rgba(0,109,119,.07);
    box-shadow:0 2px 16px rgba(0,61,79,.06);transition:box-shadow .2s,transform .2s}
  .card:hover{box-shadow:0 10px 40px rgba(0,61,79,.13);transform:translateY(-3px)}

  .tag{display:inline-flex;align-items:center;gap:6px;padding:5px 14px;
    border-radius:100px;font-size:.7rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase}
  .tag-gold{background:rgba(232,197,71,.14);border:1px solid rgba(232,197,71,.35);color:${C.gold}}
  .tag-teal{background:rgba(0,180,200,.12);border:1px solid rgba(0,180,200,.35);color:${C.pool}}
  .tag-green{background:rgba(74,222,128,.12);border:1px solid rgba(74,222,128,.35);color:#22c55e}

  .dot-live{width:7px;height:7px;border-radius:50%;background:#4ADE80;
    box-shadow:0 0 0 0 rgba(74,222,128,.5);animation:pulse 2s ease infinite}

  .shimmer{background:linear-gradient(90deg,${C.gold} 0%,#fffab8 40%,${C.gold} 60%,${C.goldDk} 100%);
    background-size:200% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;
    background-clip:text;animation:shimmer 4s linear infinite}

  .wave{overflow:hidden;line-height:0;display:block}
  .lane-bg{position:absolute;inset:0;overflow:hidden;pointer-events:none;opacity:.08}

  .feature-icon{width:46px;height:46px;border-radius:13px;display:flex;align-items:center;justify-content:center;flex-shrink:0}

  .rm-row{display:flex;align-items:center;gap:12px;padding:11px 16px;border-radius:12px;
    transition:background .15s}
  .rm-row:hover{background:rgba(0,109,119,.04)}

  .scroll-hint{animation:bounce 2.5s ease-in-out infinite}

  .nav-logo{height:96px;width:auto;max-width:460px;object-fit:contain;display:block;flex-shrink:0}
  @media(max-width:768px){.nav-logo{height:64px;max-width:280px}}

  .logo-pill{display:inline-flex;align-items:center;background:${C.cream};border-radius:12px;padding:5px 16px}

  /* hero card float */
  .hero-card{animation:floatUp 5s ease-in-out infinite}

  /* Mobile hero photo — the full-bleed photo block above (left:640, absolutely positioned
     against the header) is desktop-only (.hide-m) since it's laid out relative to the fixed
     text-column width, so below 900px it was simply never shown at all (Ram: "i can't see
     image at all in landing page when viewed in the mobile"). This is a separate, normal-flow
     image shown only on mobile instead — hidden by default, revealed inside the 900px query.
     Session 139 (Ram): the first pass had it as a padded, rounded, drop-shadowed rectangle —
     read as a floating square pasted onto the teal background, nothing like the desktop
     version's blended full-bleed treatment. Now full-bleed edge-to-edge (breaks out of the
     Wrap's own 28px padding via the vw/negative-margin trick below) with no radius/shadow,
     plus the same gradient-overlay blending the desktop photo uses, so it dissolves into the
     hero background on both edges instead of sitting on top of it as a hard rectangle. */
  .mobile-hero-photo{display:none}

  @media(max-width:900px){
    .hide-m{display:none!important}
    .g2{grid-template-columns:1fr!important}
    .hero-grid{grid-template-columns:1fr!important}
    .cta-row{flex-direction:column!important;align-items:stretch!important}
    .cta-row .btn{justify-content:center}
    .hero-text{max-width:100%!important}
    .mobile-hero-photo{
      display:block; margin-top:34px;
      width:100vw; position:relative; left:50%; right:50%;
      margin-left:-50vw; margin-right:-50vw;
    }
  }
  @media(max-width:640px){
    .g4{grid-template-columns:1fr 1fr!important}
  }
  /* Wide-monitor hero fill — the hero's Wrap is capped+flush-left (see comment in
     JSX) so on screens wider than ~1300px there's real empty space to the right of
     it. These elements only exist to fill that specific gap, so hide them below the
     width where the gap doesn't meaningfully exist yet. */
  @media(max-width:1300px){
    .wide-fill{display:none!important}
  }
  /* Shifts the hero's card/pill cluster rightward on wide monitors, proportional to
     how much space exists beyond the capped 1120px column, so it visually bridges
     the gap toward the wide-fill graphics instead of leaving a flat gap between two
     separate clusters. Capped at 420px so it never runs into the lane graphic. */
  @media(min-width:1300px){
    .wide-shift{ transform: translateX(clamp(0px, calc((100vw - 1120px) * 0.32), 420px)); }
  }
`;

// ── Reusable layout bits ──────────────────────────────────
export const LapisticsLogo = ({ className = '' }) => (
  <img src="/assets/lapistics_logo_full.png"
    srcSet="/assets/lapistics_logo_full@2x.png 2x"
    alt="Lapistics™"
    className={className}
    style={{ objectFit:'contain', display:'block', maxWidth:'100%' }}/>
);

const Wrap = ({ children, style = {} }) => (
  <div style={{ maxWidth:1120, margin:'0 auto', padding:'0 28px', ...style }}>{children}</div>
);

const Sec = ({ id, bg, children, pad='80px 0', style={}, topAccent }) => (
  <section id={id} style={{ background:bg, padding:pad, position:'relative', ...style }}>
    {/* Thin gradient seam at the very top of the section, colour-matched to what follows —
        replaces the old flat colour-block jump between sections with one deliberate accent
        line instead of a decorative divider shape. */}
    {topAccent && (
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:topAccent }}/>
    )}
    <Wrap>{children}</Wrap>
  </section>
);

// NOTE: no decorative section dividers (waves/diagonals) — flat colour-block transitions,
// same approach most premium SaaS sites use now. The "wow" lever here is the showcase
// section below, not a shape in the seam between two background colours.

const LaneBg = () => (
  <div className="lane-bg">
    {[10,22,34,46,58,70,82].map(p=>(
      <div key={p} style={{position:'absolute',top:0,bottom:0,left:`${p}%`,width:1,
        background:'rgba(232,197,71,.45)'}}/>
    ))}
    {[20,40,60,80].map(p=>(
      <div key={p} style={{position:'absolute',left:0,right:0,top:`${p}%`,height:1,
        background:'repeating-linear-gradient(90deg,rgba(255,248,237,.6) 0,rgba(255,248,237,.6) 32px,transparent 32px,transparent 52px)'}}/>
    ))}
  </div>
);

// ── Hero product card ─────────────────────────────────────
const HeroCard = () => {
  const rows = [
    { lane:4, name:'J. Patterson',  time:'1:02.47', pos:1, pb:true  },
    { lane:2, name:'M. Nguyen',     time:'1:03.21', pos:2, pb:false },
    { lane:6, name:'L. Okonkwo',    time:'1:04.08', pos:3, pb:true  },
    { lane:3, name:'S. Bradshaw',   time:'1:05.33', pos:4, pb:false },
    { lane:5, name:'T. Williams',   time:'DNS',     pos:5, pb:false },
  ];
  const posStyle = {
    1:{ bg:'rgba(232,197,71,.2)', border:'rgba(232,197,71,.6)', txt:C.goldDk },
    2:{ bg:'rgba(180,200,220,.18)', border:'rgba(180,200,220,.5)', txt:'#5a8fa8' },
    3:{ bg:'rgba(176,110,55,.14)', border:'rgba(176,110,55,.4)', txt:'#9a6030' },
  };

  return (
    <div className="hero-card" style={{
      position:'absolute', right:0, top:'50%',
      transform:'translateY(-50%)',
      width:'min(320px,27vw)', zIndex:3, pointerEvents:'none',
    }}>
      {/* Notification chip — top left. Light/cream so it reads as a floating OS-style toast
          against the card, rather than blending into the teal-family hero backdrop. */}
      <div style={{
        position:'absolute', top:-16, left:-22,
        background:'rgba(255,252,248,.98)', borderRadius:10, padding:'8px 14px',
        display:'flex', alignItems:'center', gap:8,
        boxShadow:'0 10px 28px rgba(0,20,40,.28), 0 0 0 1px rgba(0,109,119,.08)',
        whiteSpace:'nowrap',
      }}>
        <div className="dot-live"/>
        <span style={{fontSize:'.68rem',fontWeight:800,color:C.teal,letterSpacing:'.02em'}}>
          Results published
        </span>
      </div>

      {/* Main card */}
      <div style={{
        background:'rgba(255,252,248,.97)',
        borderRadius:18,
        overflow:'hidden',
        boxShadow:'0 28px 72px rgba(0,20,40,.48), 0 0 0 1px rgba(255,255,255,.12)',
      }}>
        {/* Header bar */}
        <div style={{background:C.navy, padding:'13px 16px'}}>
          <div style={{fontSize:'.6rem',fontWeight:700,color:'rgba(255,248,237,.45)',
            letterSpacing:'.12em',textTransform:'uppercase',marginBottom:3}}>
            Heat 3 · 50m Freestyle · U14 Boys
          </div>
          <div style={{fontSize:'.88rem',fontWeight:800,color:C.cream,letterSpacing:'-.02em'}}>
            Coral Bay Swim Club
          </div>
        </div>

        {/* Column headers */}
        <div style={{
          display:'grid', gridTemplateColumns:'22px 1fr 68px 32px',
          gap:6, padding:'7px 16px',
          background:'rgba(0,109,119,.05)',
          borderBottom:'1px solid rgba(0,109,119,.07)',
        }}>
          {['Ln','Swimmer','Time',''].map(h=>(
            <div key={h} style={{fontSize:'.58rem',fontWeight:700,color:C.grey,
              letterSpacing:'.1em',textTransform:'uppercase'}}>{h}</div>
          ))}
        </div>

        {/* Rows */}
        {rows.map((r,i)=>{
          const ps = posStyle[r.pos];
          return (
            <div key={r.lane} style={{
              display:'grid', gridTemplateColumns:'22px 1fr 68px 32px',
              gap:6, padding:'9px 16px', alignItems:'center',
              background: i===0 ? 'rgba(232,197,71,.05)' : 'transparent',
              borderBottom: i<rows.length-1 ? '1px solid rgba(0,109,119,.05)' : 'none',
            }}>
              <div style={{fontSize:'.72rem',fontWeight:500,color:'rgba(0,61,79,.35)'}}>{r.lane}</div>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                {ps ? (
                  <div style={{
                    width:17,height:17,borderRadius:5,flexShrink:0,
                    display:'flex',alignItems:'center',justifyContent:'center',
                    background:ps.bg, border:`1px solid ${ps.border}`,
                    fontSize:'.55rem',fontWeight:800,color:ps.txt,
                  }}>{r.pos}</div>
                ) : (
                  <div style={{width:17,height:17,flexShrink:0,
                    display:'flex',alignItems:'center',justifyContent:'center',
                    fontSize:'.6rem',fontWeight:500,color:'rgba(0,61,79,.3)'}}>{r.pos}</div>
                )}
                <span style={{fontSize:'.78rem',fontWeight:i===0?700:500,color:C.text,letterSpacing:'-.01em'}}>
                  {r.name}
                </span>
              </div>
              <div style={{
                fontSize:'.84rem', fontWeight:800,
                color: r.time==='DNS' ? '#94a3b8' : i===0 ? C.teal : C.text,
                letterSpacing:'-.02em', fontVariantNumeric:'tabular-nums',
              }}>{r.time}</div>
              <div>
                {r.pb && (
                  <div style={{
                    background:'rgba(0,180,200,.12)',border:'1px solid rgba(0,180,200,.4)',
                    borderRadius:5,padding:'2px 5px',
                    fontSize:'.52rem',fontWeight:800,color:C.pool,
                    letterSpacing:'.06em',textTransform:'uppercase',
                  }}>PB</div>
                )}
              </div>
            </div>
          );
        })}

        {/* Footer */}
        <div style={{
          padding:'9px 16px', background:'rgba(0,109,119,.04)',
          borderTop:'1px solid rgba(0,109,119,.06)',
          display:'flex', justifyContent:'space-between', alignItems:'center',
        }}>
          <span style={{fontSize:'.64rem',color:'rgba(0,61,79,.38)',fontWeight:500}}>
            5 swimmers · 2 PBs set
          </span>
          <div style={{display:'flex',gap:4}}>
            {[C.gold, C.teal, 'rgba(0,61,79,.18)'].map((c,i)=>(
              <div key={i} style={{width:5,height:5,borderRadius:'50%',background:c}}/>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

// Session 138: HeroResultsCard (the below-photo scoreboard card added in Session 136) was
// removed — it could push past the header's own bottom edge and get clipped by the header's
// overflow:hidden on tall/wide viewport combinations (Ram's screenshot showed it cut off).
// The hero photo below now stretches to fill that space directly instead.
// ── Hero satellite stat pill ─────────────────────────────────
const HeroStatPill = ({ emoji, grad, value, label, style }) => (
  <div className="hero-card" style={{ position:'absolute', zIndex:4, pointerEvents:'none', ...style }}>
    <div style={{
      background:'rgba(255,252,248,.97)', borderRadius:14, padding:'11px 15px',
      display:'flex', alignItems:'center', gap:10, whiteSpace:'nowrap',
      boxShadow:'0 16px 36px rgba(0,20,40,.4), 0 0 0 1px rgba(255,255,255,.12)',
    }}>
      <div style={{
        width:32, height:32, borderRadius:9, flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.95rem',
        background:grad, boxShadow:'0 4px 10px rgba(0,0,0,.2)',
      }}>{emoji}</div>
      <div>
        <div style={{fontSize:'.92rem', fontWeight:800, color:C.navy, letterSpacing:'-.02em', lineHeight:1.1}}>{value}</div>
        <div style={{fontSize:'.6rem', color:C.grey, fontWeight:600, marginTop:1}}>{label}</div>
      </div>
    </div>
  </div>
);

// ── Hero live-activity ticker ────────────────────────────────
// Fills the previously-empty lower-right pocket of the hero with a small,
// auto-rotating "proof of life" chip — reuses the same floating-card visual
// language as HeroStatPill, but in emerald/green (the one accent from
// RACE_TYPE_STYLE/DESIGN_SYSTEM.md not yet used in the hero), so the hero's
// colour spread now matches the purple/blue/orange/green/cyan family used
// across the rest of the app rather than stopping at three colours.
const ACTIVITY_MESSAGES = [
  { emoji:'\ud83c\udfc5', text:'New PB \u2014 J. Patterson' },
  { emoji:'\u2705', text:'12 check-ins today' },
  { emoji:'\ud83d\udce3', text:'Championship in 5 days' },
];

const HeroActivityTicker = ({ style }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % ACTIVITY_MESSAGES.length), 2600);
    return () => clearInterval(t);
  }, []);
  const m = ACTIVITY_MESSAGES[idx];
  return (
    <div className="hero-card" style={{ position:'absolute', zIndex:4, pointerEvents:'none', ...style }}>
      <div style={{
        background:'rgba(255,252,248,.97)', borderRadius:14, padding:'10px 16px',
        display:'flex', alignItems:'center', gap:10, whiteSpace:'nowrap',
        boxShadow:'0 16px 36px rgba(0,20,40,.4), 0 0 0 1px rgba(255,255,255,.12)',
      }}>
        <div style={{
          width:30, height:30, borderRadius:9, flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.88rem',
          background:'linear-gradient(135deg,#15803D 0%,#4ADE80 100%)',
          boxShadow:'0 4px 10px rgba(0,0,0,.2)',
        }}>{m.emoji}</div>
        <div key={idx} className="au" style={{fontSize:'.76rem', fontWeight:700, color:C.navy, letterSpacing:'-.01em'}}>
          {m.text}
        </div>
        <div className="dot-live" style={{marginLeft:2}}/>
      </div>
    </div>
  );
};

// ── Hero stats strip ───────────────────────────────────────
// Lives directly below the hero — replaces the HeroStatPill/HeroCard/HeroActivityTicker
// cluster that used to float over the hero illustration/photo (Session 129: moved out
// once a real photo made floating cards read as clutter rather than depth). Same four
// numbers (142/6/37/12), same bright solid-fill icon-badge treatment Ram approved earlier
// in the session (gold trophy / blue flag / red clock / green check — saturated fills,
// not muted tints) rather than reinventing the visual language from scratch.
const STATS_STRIP = [
  { icon:'🏆', bg:'#E8C547', fg:'#5c3d00', value:'142', label:'Active members' },
  { icon:'🇦🇺', bg:'#378ADD', fg:'#fff',    value:'6',   label:'Race categories' },
  { icon:'⏱',  bg:'#D85A30', fg:'#fff',    value:'37',  label:'PBs this season' },
  { icon:'✅', bg:'#1D9E75', fg:'#fff',    value:'12',  label:'Check-ins today' },
];

const HeroStatsStrip = () => (
  <section style={{ background:C.cream, padding:'28px 28px' }}>
    <Wrap>
      <div className="on-scroll" style={{
        display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16,
        padding:'20px 24px', background:'#fff', borderRadius:18,
        border:'1px solid rgba(0,109,119,.08)', boxShadow:'0 4px 20px rgba(0,61,79,.06)',
      }} className="g4">
        {STATS_STRIP.map((s,i)=>(
          <div key={i} style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{
              width:44, height:44, borderRadius:'50%', flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.15rem',
              background:s.bg, boxShadow:'0 4px 10px rgba(0,0,0,.14)',
            }}>{s.icon}</div>
            <div>
              <div style={{fontWeight:800, fontSize:'1.25rem', color:C.navy, letterSpacing:'-.02em', lineHeight:1.1}}>{s.value}</div>
              <div style={{fontSize:'.72rem', color:C.grey, fontWeight:600, marginTop:2}}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>
    </Wrap>
  </section>
);

// ── Product Showcase ───────────────────────────────────────
// Full-bleed dark section — deliberately breaks the .card grid rhythm every
// other section uses (Ram: "wow" lever). Faithful miniatures of 3 real
// in-app screens, built from the EXACT gradients already live in
// EventCard.jsx's RACE_TYPE_STYLE and Dashboard.jsx's StatCard — not
// invented colours. This is the "prove it's real, not a template" move:
// a tabbed browser-chrome frame the person can click through themselves.
const RACE_TYPE_SHOWCASE = {
  GRADED:       { icon:'🏊', grad:'linear-gradient(135deg,#0369A1 0%,#38BDF8 100%)' },
  HANDICAP:     { icon:'⏱',  grad:'linear-gradient(135deg,#C2410C 0%,#FB923C 100%)' },
  CHAMPIONSHIP: { icon:'🏆', grad:'linear-gradient(135deg,#7C3AED 0%,#C084FC 100%)' },
  RELAY:        { icon:'🤝', grad:'linear-gradient(135deg,#0891B2 0%,#22D3EE 100%)' },
};

const DASH_STATS = [
  { icon:'👥', value:'142', label:'Active Members',       grad:'linear-gradient(135deg,#006D77 0%,#00A7B5 100%)' },
  { icon:'📅', value:'8',   label:'Upcoming Events',      grad:'linear-gradient(135deg,#0369A1 0%,#38BDF8 100%)' },
  { icon:'⏱',  value:'37',  label:'PBs This Season',      grad:'linear-gradient(135deg,#C2410C 0%,#FB923C 100%)' },
  { icon:'🏆', value:'12',  label:'On the Points Ladder', grad:'linear-gradient(135deg,#7C3AED 0%,#C084FC 100%)' },
];

const SHOWCASE_EVENTS = [
  { type:'CHAMPIONSHIP', title:'Club Championship Finals', sub:'Sat 18 Jul · 6 categories', today:true },
  { type:'GRADED',       title:'Saturday Graded Meet',     sub:'Sat 25 Jul · Long course' },
  { type:'HANDICAP',     title:'Handicap Sprint Night',    sub:'Fri 31 Jul · Twilight' },
  { type:'RELAY',        title:'Medley Relay Carnival',    sub:'Sun 9 Aug · All squads' },
];

const SCOREBOARD_ROWS = [
  { lane:4, name:'J. Patterson', time:'1:02.47', pos:1, pb:true  },
  { lane:2, name:'M. Nguyen',    time:'1:03.21', pos:2, pb:false },
  { lane:6, name:'L. Okonkwo',   time:'1:04.08', pos:3, pb:true  },
];

const ShowcaseEventRow = ({ type, title, sub, today }) => {
  const rt = RACE_TYPE_SHOWCASE[type];
  const grad = today ? 'linear-gradient(135deg,#FF6B35 0%,#E63946 45%,#C9184A 100%)' : rt.grad;
  return (
    <div style={{
      background:'#fff', borderRadius:14, padding:'13px 16px',
      display:'flex', alignItems:'center', gap:12,
      boxShadow:'0 6px 18px rgba(0,20,40,.12)',
    }}>
      <div style={{
        width:40, height:40, borderRadius:10, background:grad, flexShrink:0,
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.05rem',
        boxShadow:'0 4px 10px rgba(0,0,0,.18)',
      }}>{today ? '🔥' : rt.icon}</div>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontWeight:700, fontSize:'.85rem', color:C.navy, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{title}</div>
        <div style={{fontSize:'.72rem', color:C.grey, marginTop:1}}>{sub}</div>
      </div>
      {today && (
        <div style={{
          background:'rgba(230,57,70,.12)', border:'1px solid rgba(230,57,70,.4)',
          borderRadius:100, padding:'3px 9px', fontSize:'.58rem', fontWeight:800,
          color:'#E63946', letterSpacing:'.06em', flexShrink:0,
        }}>TODAY</div>
      )}
    </div>
  );
};

const ProductShowcase = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const tabs = [
    { id:'dashboard', label:'Dashboard' },
    { id:'events',    label:'Events' },
    { id:'raceday',   label:'Race Day' },
  ];

  return (
    <section style={{
      background:`linear-gradient(160deg, ${C.navy} 0%, #011E29 100%)`,
      padding:'88px 28px 96px', position:'relative', overflow:'hidden',
    }}>
      <LaneBg/>
      <div style={{
        position:'absolute', left:'-8%', top:'8%', width:340, height:340, borderRadius:'50%',
        background:'radial-gradient(circle, rgba(124,58,237,.16) 0%, transparent 70%)', pointerEvents:'none',
      }}/>
      <div style={{
        position:'absolute', right:'-6%', bottom:'2%', width:380, height:380, borderRadius:'50%',
        background:'radial-gradient(circle, rgba(3,105,161,.16) 0%, transparent 70%)', pointerEvents:'none',
      }}/>

      <Wrap style={{position:'relative', zIndex:1}}>
        <div style={{textAlign:'center', marginBottom:44}} className="on-scroll">
          <div className="tag tag-gold" style={{marginBottom:14}}>See it in action</div>
          <h2 style={{
            fontWeight:900, fontSize:'clamp(1.8rem,3vw,2.5rem)',
            lineHeight:1.12, letterSpacing:'-.025em', color:C.cream, marginBottom:14,
          }}>
            This isn't a mockup.<br/>It's the actual product.
          </h2>
          <p style={{color:'rgba(255,248,237,.62)', fontSize:'.98rem', maxWidth:480, margin:'0 auto'}}>
            Real screens, real gradients, the same colour system live in the app your
            club would actually log into. Click a tab.
          </p>
        </div>

        <div className="on-scroll" style={{
          maxWidth:640, margin:'0 auto', borderRadius:20, overflow:'hidden',
          boxShadow:'0 40px 100px rgba(0,0,0,.5), 0 0 0 1px rgba(255,255,255,.06)',
          transform:'perspective(1400px) rotateX(2deg)',
        }}>
          {/* Browser chrome */}
          <div style={{background:'#1a2c35', padding:'11px 16px', display:'flex', alignItems:'center', gap:16}}>
            <div style={{display:'flex', gap:6}}>
              {['#FF5F57','#FEBC2E','#28C840'].map(c=>(
                <div key={c} style={{width:10, height:10, borderRadius:'50%', background:c}}/>
              ))}
            </div>
            <div style={{display:'flex', gap:6, flex:1, justifyContent:'center'}}>
              {tabs.map(t=>(
                <button key={t.id} onClick={()=>setTab(t.id)} style={{
                  background: tab===t.id ? 'rgba(255,255,255,.14)' : 'transparent',
                  border:'none', borderRadius:100, padding:'6px 16px',
                  color: tab===t.id ? C.cream : 'rgba(255,248,237,.4)',
                  fontSize:'.76rem', fontWeight:700, cursor:'pointer',
                  fontFamily:"'Outfit',sans-serif", transition:'all .15s',
                }}>{t.label}</button>
              ))}
            </div>
            <div style={{width:44}}/>
          </div>

          {/* Content */}
          <div style={{background:C.cream, padding:'26px 22px', minHeight:320}}>
            {tab === 'dashboard' && (
              <div className="au" key="dashboard">
                <div style={{fontSize:'.7rem', fontWeight:700, color:C.grey, letterSpacing:'.08em', textTransform:'uppercase', marginBottom:14}}>
                  Coral Bay Swim Club · Dashboard
                </div>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                  {DASH_STATS.map((s,i)=>(
                    <div key={i} style={{
                      background:'#fff', borderRadius:14, padding:'16px 14px',
                      textAlign:'center', boxShadow:'0 4px 14px rgba(0,20,40,.08)',
                    }}>
                      <div style={{
                        width:38, height:38, borderRadius:'50%', margin:'0 auto 8px',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        background:s.grad, fontSize:'1rem', boxShadow:'0 4px 10px rgba(0,0,0,.18)',
                      }}>{s.icon}</div>
                      <div style={{fontWeight:800, fontSize:'1.3rem', color:C.navy, letterSpacing:'-.02em'}}>{s.value}</div>
                      <div style={{fontSize:'.68rem', color:C.grey, fontWeight:600, marginTop:2}}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'events' && (
              <div className="au" key="events">
                <div style={{fontSize:'.7rem', fontWeight:700, color:C.grey, letterSpacing:'.08em', textTransform:'uppercase', marginBottom:14}}>
                  Upcoming Events
                </div>
                <div style={{display:'grid', gap:10}}>
                  {SHOWCASE_EVENTS.map((e,i)=>(<ShowcaseEventRow key={i} {...e}/>))}
                </div>
              </div>
            )}

            {tab === 'raceday' && (
              <div className="au" key="raceday">
                <div style={{background:C.navy, borderRadius:'12px 12px 0 0', padding:'12px 16px'}}>
                  <div style={{
                    fontSize:'.6rem', fontWeight:700, color:'rgba(255,248,237,.45)',
                    letterSpacing:'.1em', textTransform:'uppercase', marginBottom:3,
                  }}>
                    Heat 3 · 50m Freestyle · U14 Boys
                  </div>
                  <div style={{fontSize:'.85rem', fontWeight:800, color:C.cream}}>Live Scoreboard</div>
                </div>
                <div style={{background:'#fff', borderRadius:'0 0 12px 12px', overflow:'hidden', boxShadow:'0 4px 14px rgba(0,20,40,.1)'}}>
                  {SCOREBOARD_ROWS.map((r,i)=>(
                    <div key={r.lane} style={{
                      display:'grid', gridTemplateColumns:'24px 1fr 70px 32px', gap:8,
                      padding:'11px 16px', alignItems:'center',
                      background: i===0 ? 'rgba(232,197,71,.06)' : 'transparent',
                      borderBottom: i<SCOREBOARD_ROWS.length-1 ? '1px solid rgba(0,109,119,.06)' : 'none',
                    }}>
                      <div style={{fontSize:'.72rem', color:'rgba(0,61,79,.35)'}}>{r.lane}</div>
                      <div style={{display:'flex', alignItems:'center', gap:6}}>
                        <div style={{
                          width:18, height:18, borderRadius:5, display:'flex', alignItems:'center', justifyContent:'center',
                          background: r.pos===1 ? 'rgba(232,197,71,.2)' : 'rgba(180,200,220,.18)',
                          border: r.pos===1 ? '1px solid rgba(232,197,71,.6)' : '1px solid rgba(180,200,220,.5)',
                          fontSize:'.56rem', fontWeight:800, color: r.pos===1 ? C.goldDk : '#5a8fa8',
                        }}>{r.pos}</div>
                        <span style={{fontSize:'.82rem', fontWeight:i===0?700:500, color:C.text}}>{r.name}</span>
                      </div>
                      <div style={{fontSize:'.84rem', fontWeight:800, color:i===0?C.teal:C.text}}>{r.time}</div>
                      <div>
                        {r.pb && (
                          <div style={{
                            background:'rgba(0,180,200,.12)', border:'1px solid rgba(0,180,200,.4)',
                            borderRadius:5, padding:'2px 5px', fontSize:'.5rem', fontWeight:800, color:C.pool,
                          }}>PB</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="on-scroll" style={{textAlign:'center', marginTop:32}}>
          <button className="btn btn-gold" onClick={()=>nav('/demo')}>🏊 &nbsp;Explore the Full Live Demo</button>
        </div>
      </Wrap>
    </section>
  );
};

// ── Main ─────────────────────────────────────────────────
export default function Landing() {
  const nav = useNavigate();
  const heroRef = useRef(null);

  useEffect(() => {
    const els = document.querySelectorAll('.on-scroll');
    if (!('IntersectionObserver' in window)) { els.forEach(e=>e.classList.add('au')); return; }
    const io = new IntersectionObserver(entries=>{
      entries.forEach(e=>{ if(e.isIntersecting){ e.target.classList.add('au'); io.unobserve(e.target); }});
    },{ threshold:.1 });
    els.forEach(e=>io.observe(e));
    return ()=>io.disconnect();
  },[]);

  // Icon tiles now pull from the SAME multi-colour gradient family already approved and
  // live in the real app (DESIGN_SYSTEM.md §2 RACE_TYPE_STYLE: blue/orange/purple/green/
  // cyan) instead of either random pastels or one flat teal — richer colour, but still a
  // deliberate, reused system rather than invented-on-the-spot swatches. Gold stays as the
  // one constant accent across all six (the check/arrow details) to tie the row together.
  const features = [
    { bg:'rgba(0,109,119,.08)', accent:'#006D77', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><rect x="2" y="5" width="20" height="14" rx="3" stroke="#006D77" strokeWidth="1.7"/><path d="M2 10h20" stroke="#006D77" strokeWidth="1.7"/><path d="M7 15l2 2 6-6" stroke="#E8C547" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      label:'Race Day Management', desc:'Create events, build heats and publish results in one continuous flow — from setup to scoreboard.' },
    { bg:'rgba(3,105,161,.08)', accent:'#0369A1', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><polyline points="3,17 8,11 13,14 21,6" stroke="#0369A1" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><circle cx="21" cy="6" r="2" fill="#E8C547"/><line x1="3" y1="21" x2="21" y2="21" stroke="#0369A1" strokeWidth="1.4"/></svg>,
      label:'Performance Tracking', desc:'PBs, age-group rankings and season points update the moment a result is recorded — no manual entry.' },
    { bg:'rgba(124,58,237,.08)', accent:'#7C3AED', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><circle cx="12" cy="7" r="4" stroke="#7C3AED" strokeWidth="1.7"/><path d="M4 20c0-3.5 3.6-6 8-6s8 2.5 8 6" stroke="#7C3AED" strokeWidth="1.7" strokeLinecap="round"/><circle cx="20" cy="10" r="3" stroke="#E8C547" strokeWidth="1.4"/></svg>,
      label:'Parent Visibility', desc:"Parents see their child's entries, times and progress in real time — no chasing the coach after a meet." },
    { bg:'rgba(21,128,61,.08)', accent:'#15803D', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><rect x="4" y="3" width="16" height="18" rx="3" stroke="#15803D" strokeWidth="1.7"/><path d="M8 10l3 3 6-6" stroke="#E8C547" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><line x1="8" y1="17" x2="16" y2="17" stroke="#15803D" strokeWidth="1.3" strokeLinecap="round"/></svg>,
      label:'Attendance & Check-in', desc:'Race-day check-in and training attendance in one tap. Instant roll call, permanent records, zero paper.' },
    { bg:'rgba(8,145,178,.08)', accent:'#0891B2', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><rect x="3" y="3" width="12" height="16" rx="2" stroke="#0891B2" strokeWidth="1.7"/><path d="M9 3v16" stroke="#0891B2" strokeWidth="1.1" strokeDasharray="2 2"/><path d="M16 9l5 3-5 3" stroke="#E8C547" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      label:'Bulk Member Import', desc:'Upload your full roster from a spreadsheet in minutes. Row-by-row error flagging before anything saves.' },
    { bg:'rgba(194,65,12,.08)', accent:'#C2410C', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><rect x="5" y="3" width="14" height="18" rx="3" stroke="#C2410C" strokeWidth="1.7"/><circle cx="12" cy="12" r="3" stroke="#E8C547" strokeWidth="1.6"/><path d="M9 18h6" stroke="#C2410C" strokeWidth="1.3" strokeLinecap="round"/><path d="M8 7h8" stroke="#C2410C" strokeWidth="1.1" strokeLinecap="round"/></svg>,
      label:'Role-based Access', desc:'Admins, coaches, timekeepers, parents and swimmers each see exactly what they need — nothing more.' },
    // Session 136: three cards added (Ram: "review the text added and update as required
    // with more outstanding points if anything is not specified well") — these three real,
    // already-built capabilities (StarterMode.jsx's RAF timer, the Awards Night podium/
    // Most Improved/Club Champion computations, the DQ reason-capture dialog) had no
    // feature card at all despite the roadmap section below already marking two of them
    // "done". Rounds the grid out to a clean 3x3 instead of an odd 6. Colours cycle back
    // through the same teal/blue/purple already used on cards 1-3 (deliberate — same
    // approved palette, not new colours), so column alignment stays consistent top-to-bottom.
    { bg:'rgba(0,109,119,.08)', accent:'#006D77', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><circle cx="12" cy="13" r="8" stroke="#006D77" strokeWidth="1.7"/><path d="M12 13V8" stroke="#006D77" strokeWidth="1.7" strokeLinecap="round"/><path d="M9 3h6" stroke="#006D77" strokeWidth="1.7" strokeLinecap="round"/><path d="M12 13l3 2" stroke="#E8C547" strokeWidth="2" strokeLinecap="round"/></svg>,
      label:'Precision Race-Day Timing', desc:'Frame-accurate stopwatch timing, run from any phone or tablet on deck — no separate timing hardware to buy.' },
    { bg:'rgba(3,105,161,.08)', accent:'#0369A1', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M7 4h10v5a5 5 0 0 1-10 0V4z" stroke="#0369A1" strokeWidth="1.7"/><path d="M7 5H4a3 3 0 0 0 3 5" stroke="#0369A1" strokeWidth="1.5" strokeLinecap="round"/><path d="M17 5h3a3 3 0 0 1-3 5" stroke="#0369A1" strokeWidth="1.5" strokeLinecap="round"/><path d="M12 14v4" stroke="#0369A1" strokeWidth="1.7"/><path d="M8 21h8" stroke="#0369A1" strokeWidth="1.7" strokeLinecap="round"/><circle cx="12" cy="9" r="2" fill="#E8C547"/></svg>,
      label:'Season Awards & Points', desc:"Podiums, Most Improved and Age/Club Champion awards calculated automatically from the season's results — no spreadsheet marathon in December." },
    { bg:'rgba(124,58,237,.08)', accent:'#7C3AED', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M5 21V4" stroke="#7C3AED" strokeWidth="1.7" strokeLinecap="round"/><path d="M5 4h13l-3 4 3 4H5" stroke="#7C3AED" strokeWidth="1.7" strokeLinejoin="round"/><circle cx="18" cy="18" r="3.2" fill="#E8C547"/><path d="M18 16.6v1.6M18 19.6h.01" stroke="#5c3d00" strokeWidth="1.3" strokeLinecap="round"/></svg>,
      label:'DQ & Race-Day Notes', desc:'One-tap disqualification capture with a reason code, timestamped and attached to the result — no more disputes about lane 4.' },
    // Session 179 (Ram: landing page needs to reflect the relay + multi-round work that's
    // shipped since the last content pass) — three more real, already-built capabilities
    // that had no card at all. Same reused accent colours as the row above (orange/green/
    // cyan), not new ones — keeps the whole grid's colour rhythm consistent front to back.
    { bg:'rgba(194,65,12,.08)', accent:'#C2410C', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M12 3v6l4 2" stroke="#C2410C" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="13" r="8" stroke="#C2410C" strokeWidth="1.7"/><path d="M9 20l1.5 -3M15 20l-1.5-3" stroke="#E8C547" strokeWidth="1.8" strokeLinecap="round"/></svg>,
      label:'Handicap Racing, Done Properly', desc:'Staggered starts calculated automatically from personal bests — a genuinely Australian club race format, built in rather than bolted on.' },
    { bg:'rgba(21,128,61,.08)', accent:'#15803D', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><circle cx="8" cy="7" r="3" stroke="#15803D" strokeWidth="1.6"/><circle cx="16" cy="7" r="3" stroke="#15803D" strokeWidth="1.6"/><path d="M3 20c0-3 2.5-5 5-5s5 2 5 5" stroke="#15803D" strokeWidth="1.6" strokeLinecap="round"/><path d="M13 20c0-3 2.5-5 5-5s3.5 1.4 3.5 3.4" stroke="#15803D" strokeWidth="1.6" strokeLinecap="round"/><circle cx="19" cy="18.4" r="2.4" fill="#E8C547"/></svg>,
      label:'Relay Teams & Rosters', desc:'Build relay rosters leg by leg, with medley stroke order enforced automatically and per-leg DQ tracking — no more "whose fault was it" arguments.' },
    { bg:'rgba(8,145,178,.08)', accent:'#0891B2', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><rect x="3" y="5" width="7" height="7" rx="1.5" stroke="#0891B2" strokeWidth="1.7"/><rect x="14" y="12" width="7" height="7" rx="1.5" stroke="#0891B2" strokeWidth="1.7"/><path d="M10 8.5h4a3 3 0 0 1 3 3v.5" stroke="#E8C547" strokeWidth="1.8" strokeLinecap="round"/></svg>,
      label:'Heats, Then Finals', desc:'Run prelim heats, then advance your top qualifiers into a Final in one click — each round scored independently, no manual re-entry.' },
    // Session 191 (Ram: landing page needs to reflect everything the app now does before a
    // pilot handover) — two more real, already-built capabilities with no card at all: data
    // portability (CSV/PDF exports, full-backup ZIP, D13) and printable race-day documents
    // (heat sheets, club records noticeboard PDF). Both are genuine trust/self-service
    // features a club evaluating the product would want to know exist. Reused accent colours
    // (teal/navy) rather than introducing new ones, keeping the grid's palette closed.
    { bg:'rgba(0,109,119,.08)', accent:'#006D77', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M12 3v12" stroke="#006D77" strokeWidth="1.7" strokeLinecap="round"/><path d="M7 10l5 5 5-5" stroke="#006D77" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 16.5V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2.5" stroke="#E8C547" strokeWidth="1.8" strokeLinecap="round"/></svg>,
      label:'Your Data, Always Exportable', desc:'Full CSV exports for members, results, attendance and club records, plus a one-click full-backup ZIP — your club\u2019s data is never locked in.' },
    { bg:'rgba(2,47,63,.08)', accent:'#012F3F', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><rect x="4" y="3" width="16" height="18" rx="2" stroke="#012F3F" strokeWidth="1.7"/><path d="M8 8h8M8 12h8M8 16h5" stroke="#012F3F" strokeWidth="1.4" strokeLinecap="round"/><circle cx="17.5" cy="17.5" r="3.2" fill="#E8C547"/><path d="M16.3 17.5l.9.9 1.6-1.7" stroke="#5c3d00" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      label:'Printable Heat Sheets & Records', desc:'Branded, print-ready PDFs for the marshalling table (lane draws by heat) and a noticeboard-style club records sheet — no manual formatting.' },
    // Session 197 (Ram: pre-pilot content review) — two more real, already-built
    // capabilities that had no card: self-service Club Settings (rename, location,
    // lane count, seasons — previously only changeable via platform support) and
    // Interclub Results (a reference log for times swum at another club's meet).
    // Reused accent colours (navy/pink-red) rather than new ones.
    { bg:'rgba(2,47,63,.08)', accent:'#012F3F', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><circle cx="12" cy="12" r="3" stroke="#012F3F" strokeWidth="1.7"/><path d="M19.4 13a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V19a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H4a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H10a1.65 1.65 0 0 0 1-1.51V4a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V10a1.65 1.65 0 0 0 1.51 1H20a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="#E8C547" strokeWidth="1.3" strokeLinejoin="round"/></svg>,
      label:'Self-Service Club Settings', desc:'Rename your club, update your location and lane count, and manage seasons — straight from Club Settings, no support ticket required.' },
    { bg:'rgba(183,28,28,.08)', accent:'#B71C1C', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M12 2l7 4v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-4z" stroke="#B71C1C" strokeWidth="1.7" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="#E8C547" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      label:'Interclub Results, On File', desc:"Log a swimmer's times from a zone carnival or another club's meet in one place — kept for reference, separate from your own club's points and records." },
    // Session 198 (Ram: apply the swim record PDF change to the landing page too) —
    // downloadSwimRecordPdf was already live (see api.js) but had no card anywhere.
    // A branded, printable per-swimmer PDF is a real trust/portability feature
    // parents care about (proof of times for trials, scholarships, keepsakes) —
    // rounds the grid to 18. Indigo accent — not yet used elsewhere in this grid.
    { bg:'rgba(92,107,192,.08)', accent:'#5C6BC0', icon:<svg viewBox="0 0 24 24" fill="none" width="22" height="22"><rect x="5" y="2" width="14" height="18" rx="2" stroke="#5C6BC0" strokeWidth="1.7"/><path d="M8 7h8M8 10.5h8M8 14h5" stroke="#5C6BC0" strokeWidth="1.3" strokeLinecap="round"/><path d="M12 22v-5.5M9.3 19.2 12 22l2.7-2.8" stroke="#E8C547" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      label:'A Swim Record They Can Keep', desc:'A branded, printable PDF of any swimmer\u2019s personal bests, results and attendance \u2014 handy for trials elsewhere, a scholarship application, or just a season keepsake.' },
  ];

  const audiences = [
    { emoji:'🏊', title:'Competitive Swimmers', desc:'PBs, race entries and age-group standings — tracked automatically, always available.', tint:'#E4F1FB' },
    { emoji:'🏛️', title:'Clubs & Squads', desc:'Member roster, events and championship points in one place. No more spreadsheets.', tint:'#F3EAFE' },
    { emoji:'🎽', title:'Coaches & Admins', desc:'Run race days, manage squads and track attendance without the paperwork scramble.', tint:'#FDECE0' },
    { emoji:'👨‍👩‍👧', title:'Parents', desc:"Follow your child's swim journey from first race to podium, times always up to date.", tint:'#E7F7EA' },
  ];

  const roadmap = [
    { done:true,  label:'Member management & bulk import' },
    { done:true,  label:'Race events & heat generation' },
    { done:true,  label:'Results recording & personal bests' },
    { done:true,  label:'Parent visibility & child linking' },
    { done:true,  label:'Attendance & race-day check-in' },
    { done:true,  label:'Season awards & championship points' },
    { done:true,  label:'Handicap racing with automatic offsets' },
    { done:true,  label:'Relay teams, rosters & leg-by-leg scoring' },
    { done:true,  label:'Heats then Finals (multi-round races)' },
    { done:true,  label:'Data exports, backups & printable heat sheets' },
    { done:false, label:'Native mobile app for swimmers & parents' },
    { done:false, label:'Club analytics & performance dashboards' },
    { done:false, label:'Cross-club & state association reporting' },
  ];

  return (
    <>
      <style>{css}</style>

      {/* ── NAV ─────────────────────────────────────── */}
      <nav style={{
        position:'fixed', top:0, left:0, right:0, zIndex:200,
        background:C.cream, borderBottom:`1px solid rgba(0,109,119,.12)`,
        boxShadow:'0 1px 16px rgba(0,61,79,.08)',
        height:108, display:'flex', alignItems:'center',
      }}>
        <div style={{
          width:'100%', padding:'0 28px',
          display:'flex', alignItems:'center', justifyContent:'space-between',
        }}>
          <div style={{cursor:'pointer'}} onClick={()=>window.scrollTo({top:0,behavior:'smooth'})}>
            <LapisticsLogo className="nav-logo"/>
          </div>

          <div style={{display:'flex',alignItems:'center',gap:28}} className="hide-m">
            {[['#features','Features'],['#who','For clubs'],['#status','Roadmap']].map(([h,l])=>(
              <a key={h} href={h} style={{
                color:C.text, fontSize:'.88rem', fontWeight:600,
                textDecoration:'none', transition:'color .15s',
              }}
              onMouseOver={e=>e.currentTarget.style.color=C.teal}
              onMouseOut={e=>e.currentTarget.style.color=C.text}>
                {l}
              </a>
            ))}
          </div>

          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            <button className="btn btn-ghost-dk" style={{padding:'8px 20px',fontSize:'.84rem'}}
              onClick={()=>nav('/login')}>Sign in</button>
            <button className="btn btn-gold" style={{padding:'8px 20px',fontSize:'.84rem'}}
              onClick={()=>nav('/get-started')}>Get early access</button>
          </div>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────── */}
      <header ref={heroRef} style={{
        background:`linear-gradient(150deg, ${C.navy} 0%, ${C.navyMid} 48%, ${C.teal} 100%)`,
        minHeight:'100vh', position:'relative',
        display:'flex', alignItems:'center', overflow:'hidden',
        paddingTop:108,
      }}>
        <LaneBg/>

        {/* Colour orbs — the hero was the flattest section on the page (just the navy-teal
            plane + faint lane-grid). These pull the same race-type palette used everywhere
            else on the page (blue/purple/orange/cyan) into soft, blurred, slow-drifting
            shapes well behind the text/card — depth and colour without competing for
            attention with the copy. */}
        <div className="orb orb-a" style={{
          right:'-60px', top:'18%', width:340, height:340,
          background:'radial-gradient(circle, rgba(0,167,181,.14) 0%, transparent 72%)',
        }}/>
        <div className="orb orb-b" style={{
          left:'-90px', top:'58%', width:280, height:280,
          background:'radial-gradient(circle, rgba(124,58,237,.13) 0%, transparent 72%)',
        }}/>
        <div className="orb orb-c" style={{
          left:'22%', top:'-8%', width:220, height:220,
          background:'radial-gradient(circle, rgba(194,65,12,.12) 0%, transparent 72%)',
        }}/>
        <div className="orb orb-b" style={{
          right:'8%', bottom:'-6%', width:200, height:200, animationDuration:'19s',
          background:'radial-gradient(circle, rgba(3,105,161,.13) 0%, transparent 72%)',
        }}/>

        {/* Wide-monitor fill — on screens wider than the hero's capped 1120px Wrap,
            there's genuine empty navy to the right of everything (Ram's screenshot,
            2550px viewport). Positioned against the header itself (not the Wrap), in
            vw units, so it only lands in that true dead zone and scales with it —
            invisible on normal laptop widths where the zone doesn't exist. New colours
            here on purpose (coral, sky blue) rather than reusing teal/gold again, so
            the far edge of a wide screen still feels intentional rather than a fainter
            repeat of the same three hues. */}
        <div className="wide-fill orb orb-a" style={{
          left:'64vw', top:'6%', width:460, height:460, animationDuration:'23s',
          background:'radial-gradient(circle, rgba(255,138,101,.17) 0%, transparent 70%)',
        }}/>
        <div className="wide-fill orb orb-c" style={{
          left:'80vw', top:'50%', width:380, height:380, animationDuration:'26s',
          background:'radial-gradient(circle, rgba(56,189,248,.15) 0%, transparent 70%)',
        }}/>
        <div className="wide-fill orb orb-b" style={{
          left:'70vw', bottom:'-10%', width:340, height:340, animationDuration:'20s',
          background:'radial-gradient(circle, rgba(232,197,71,.14) 0%, transparent 70%)',
        }}/>
        {/* Large ambient lane-perspective graphic, anchored to the true right edge of
            the viewport (not the Wrap) so it's the visual anchor for that far-right
            band on wide screens — converging pool lanes in a coral→gold→teal fade,
            echoing LaneBg's grid motif at a much larger, more painterly scale. Very
            low opacity so it reads as texture, not competing UI. */}
        <svg className="wide-fill" viewBox="0 0 600 900" preserveAspectRatio="xMidYMid slice" style={{
          position:'absolute', right:0, top:0, height:'100%', width:'36vw', minWidth:360,
          opacity:.12, pointerEvents:'none',
        }}>
          <defs>
            <linearGradient id="laneFade" x1="0" y1="1" x2="1" y2="0">
              <stop offset="0%" stopColor="#FF8A65"/>
              <stop offset="48%" stopColor="#E8C547"/>
              <stop offset="100%" stopColor="#00A7B5"/>
            </linearGradient>
          </defs>
          {[0,1,2,3,4,5,6,7].map(i=>(
            <line key={i} x1={-260+i*150} y1="920" x2={260+i*80} y2="-20" stroke="url(#laneFade)" strokeWidth="2.5"/>
          ))}
        </svg>

        {/* Full-bleed hero photo column. Outer div is the absolutely-positioned block
            (left:640 = text column's fixed right edge + grid gap, so it starts right where
            the text ends on any viewport ≥900px — see the long comment history in git blame
            for how that number was derived). Session 138: stretches from top:108 to bottom:0
            of the header itself, so the photo always exactly fills the available height —
            no separate card stacked below it that can run past the header's own bottom edge
            and get clipped by overflow:hidden. */}
        <div style={{
          position:'absolute', top:108, right:0, left:640, bottom:0, zIndex:1, pointerEvents:'none',
        }} className="hide-m">
          {/* Session 138 (Ram): the old aspectRatio-based box + HeroResultsCard stacked below it
              could add up to more vertical space than the header actually had (header height is
              set by minHeight:100vh, not by this absolutely-positioned block, so it never grows
              to fit them) — on tall/wide combinations the card got clipped by the header's own
              overflow:hidden right at the section boundary. Fix: this block now stretches exactly
              to the header's own bottom edge (top:108 to bottom:0) and the photo fills it with
              objectFit:cover, so the image always exactly fills the available gap with nothing left
              over and nothing to clip — no separate results card floating below it. */}
          <div style={{ position:'relative', width:'100%', height:'100%', overflow:'hidden' }}>
            <img src="/assets/landing_hero_photo.jpg" alt=""
              style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'center', display:'block' }}/>
            {/* Inner-edge fade only — right/top/bottom stay true full-bleed to the viewport
                edges; only the edge facing the text column dissolves into the hero's own navy,
                so the photo reads as filling the space rather than a hard rectangle pasted
                over it. */}
            <div style={{
              position:'absolute', inset:0,
              background:`linear-gradient(90deg, ${C.navy} 0%, rgba(0,61,79,.55) 10%, transparent 26%)`,
            }}/>
            {/* Colour-blend overlay — ties the photo's colours to the hero's own navy/teal
                gradient so it reads as part of the same scene, not a foreign rectangle. Kept
                light (.16, down from an earlier .45) so the photo's own brightness isn't
                crushed (Ram: "brightness got decreased… looks so dull"). */}
            <div style={{
              position:'absolute', inset:0,
              background:`linear-gradient(150deg, ${C.navy} 0%, ${C.navyMid} 48%, ${C.teal} 100%)`,
              mixBlendMode:'multiply', opacity:.16,
            }}/>
            {/* Gentle bottom-up darken for depth, kept short so it only grounds the very
                bottom edge instead of dimming most of the frame. */}
            <div style={{
              position:'absolute', inset:0,
              background:'linear-gradient(0deg, rgba(0,20,30,.18) 0%, transparent 24%)',
            }}/>
          </div>
        </div>

        {/* margin:0 (instead of Wrap's default '0 auto') — flush-left so the hero content
            starts at exactly the same x-position as the nav logo (nav's own container is
            a plain full-width div with the same 28px padding, not a centered Wrap). Without
            this, on any viewport wider than 1176px the centred 1120px Wrap pushes the hero
            text well past the logo's left edge, leaving a large empty gap on the left that
            grows with screen width. maxWidth stays 1120 so line length is still capped on
            very wide monitors — the freed-up space now sits on the right instead of being
            split awkwardly between both sides. */}
        <Wrap style={{position:'relative',zIndex:2,paddingTop:48,paddingBottom:80,width:'100%',margin:0}}>
          <div className="hero-grid" style={{display:'grid',gridTemplateColumns:'minmax(0,580px) 1fr',alignItems:'center',gap:32}}>
          <div className="hero-text">

            {/* Beta badge */}
            <div className="au" style={{
              display:'inline-flex', alignItems:'center', gap:7, marginBottom:24,
              background:'rgba(232,197,71,.12)', border:'1px solid rgba(232,197,71,.3)',
              borderRadius:100, padding:'6px 15px',
            }}>
              <div className="dot-live"/>
              <span style={{fontSize:'.69rem',fontWeight:700,color:C.gold,letterSpacing:'.11em',textTransform:'uppercase'}}>
                Early access · Register your interest
              </span>
            </div>

            <h1 className="au d1" style={{
              fontFamily:"'Outfit',sans-serif", fontWeight:900,
              fontSize:'clamp(2.6rem,5vw,4rem)', lineHeight:1.07,
              letterSpacing:'-.03em', color:C.cream, marginBottom:20,
            }}>
              Swim club management,<br/>
              <span className="shimmer">built for Australia.</span>
            </h1>

            <p className="au d2" style={{
              fontSize:'clamp(.98rem,1.6vw,1.1rem)', color:'rgba(255,248,237,.68)',
              lineHeight:1.74, marginBottom:36, maxWidth:480,
            }}>
              Race days, member records, results and parent visibility —
              purpose-built for Australian swim clubs, from the pool up.
            </p>

            <div className="au d3 cta-row" style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:18}}>
              <button className="btn btn-gold" style={{fontSize:'.97rem',padding:'14px 30px'}}
                onClick={()=>nav('/demo')}>
                🏊 &nbsp;Explore Live Demo
              </button>
              <button className="btn btn-outline" style={{fontSize:'.9rem'}}
                onClick={()=>nav('/get-started')}>
                Request Early Access →
              </button>
            </div>

            <div className="au d4" style={{fontSize:'.75rem',color:'rgba(255,248,237,.38)',marginBottom:44}}>
              No account needed · Live demo data · Resets daily
            </div>

            {/* Stats strip */}
            <div className="au d5" style={{
              display:'flex', flexWrap:'wrap', gap:0,
              paddingTop:24, borderTop:'1px solid rgba(255,248,237,.1)',
            }}>
              {[['6','Race categories per event'],['Founder','Pricing for early clubs'],['AU','Built for Australia']].map(([n,l],i)=>(
                <div key={n} style={{
                  paddingRight:24, marginRight:24,
                  borderRight: i<2 ? '1px solid rgba(255,248,237,.1)' : 'none',
                }}>
                  <div style={{fontWeight:900,fontSize:'1.45rem',color:C.gold,letterSpacing:'-.03em',lineHeight:1}}>{n}</div>
                  <div style={{fontSize:'.66rem',color:'rgba(255,248,237,.38)',textTransform:'uppercase',letterSpacing:'.1em',marginTop:3}}>{l}</div>
                </div>
              ))}
            </div>

            {/* Mobile-only hero photo — desktop shows the full-bleed photo positioned against
                the header (see .hide-m block below); that block is laid out relative to the
                fixed 640px text-column width and simply doesn't apply below 900px, so mobile
                had no hero image at all. This normal-flow, full-bleed version fills that gap
                with the same blended-into-the-scene treatment as the desktop photo (gradient
                overlays top/bottom, colour-blend multiply tying it to the hero's own navy/teal
                gradient) instead of a rounded, shadowed rectangle sitting on top of it. */}
            <div className="mobile-hero-photo">
              <div style={{ position:'relative', overflow:'hidden', lineHeight:0 }}>
                <img src="/assets/landing_hero_photo.jpg"
                  alt="Timekeeper reviewing live race results pool side"
                  style={{ width:'100%', display:'block', objectFit:'cover' }}/>
                {/* Top fade — dissolves the photo's top edge into the hero's own navy, same
                    role as the desktop version's inner-edge fade. */}
                <div style={{
                  position:'absolute', inset:0,
                  background:`linear-gradient(180deg, ${C.navy} 0%, rgba(0,61,79,.45) 7%, transparent 20%)`,
                }}/>
                {/* Colour-blend overlay — identical treatment to the desktop photo, ties the
                    photo's own colours to the hero's navy/teal gradient so it reads as part of
                    the same scene rather than a foreign rectangle. */}
                <div style={{
                  position:'absolute', inset:0,
                  background:`linear-gradient(150deg, ${C.navy} 0%, ${C.navyMid} 48%, ${C.teal} 100%)`,
                  mixBlendMode:'multiply', opacity:.16,
                }}/>
                {/* Bottom fade — dissolves into the hero's own teal so the image ends softly
                    instead of a hard edge before the next block of text/section. */}
                <div style={{
                  position:'absolute', inset:0,
                  background:`linear-gradient(0deg, ${C.teal} 0%, rgba(0,109,119,.35) 10%, transparent 26%)`,
                }}/>
              </div>
            </div>
          </div>
          {/* Empty spacer — keeps the grid's second column (1fr) reserving the same
              horizontal space for the text column's max-width as before. The actual photo
              is no longer inside this grid column at all (see full-bleed block below,
              positioned directly against <header>) — Session 132: the photo needs to fill
              the entire right half of the viewport, not just the width available inside
              this column once the Wrap's own max-width:1120 and padding are subtracted. */}
          <div className="hide-m" aria-hidden="true"/>
          </div>
        </Wrap>

        {/* Scroll hint */}
        <div className="scroll-hint" style={{
          position:'absolute', bottom:24, left:'50%', transform:'translateX(-50%)',
          display:'flex', flexDirection:'column', alignItems:'center', gap:5,
          opacity:.3, pointerEvents:'none',
        }}>
          <span style={{fontSize:'.62rem',color:C.cream,letterSpacing:'.14em',textTransform:'uppercase'}}>scroll</span>
          <svg width="14" height="22" viewBox="0 0 14 22" fill="none">
            <rect x="1" y="1" width="12" height="20" rx="6" stroke={C.cream} strokeWidth="1.2"/>
            <rect x="6" y="4" width="2" height="5" rx="1" fill={C.cream}/>
          </svg>
        </div>

        {/* flat transition — no divider shape */}
      </header>

      {/* ── PROBLEM ─────────────────────────────────── */}
      <Sec bg={C.cream} topAccent={`linear-gradient(90deg, ${C.navy} 0%, ${C.teal} 50%, ${C.pool} 100%)`}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:64,alignItems:'center'}} className="g2">
          <div className="on-scroll">
            <div className="tag tag-teal" style={{marginBottom:14}}>The problem</div>
            <h2 style={{fontWeight:800,fontSize:'clamp(1.8rem,2.8vw,2.5rem)',
              lineHeight:1.12,letterSpacing:'-.025em',color:C.navy,marginBottom:18}}>
              Most clubs are still<br/>running on spreadsheets
            </h2>
            <p style={{color:C.grey,lineHeight:1.78,fontSize:'.98rem',marginBottom:14}}>
              Australian swim clubs work incredibly hard — but most still rely on spreadsheets,
              paper sheets and disconnected group chats to manage members, record times and keep
              parents informed.
            </p>
            <p style={{color:C.grey,lineHeight:1.78,fontSize:'.98rem'}}>
              Every race day is a scramble. Results vanish after the meet. Admins spend
              weekends on data entry instead of being at the pool.
            </p>
          </div>
          <div style={{display:'grid',gap:10}}>
            {[
              { t:'Race results scattered across emails and paper' },
              { t:'Parents chasing times through group chats' },
              { t:"Spreadsheets nobody trusts after week two" },
              { t:'No single source of truth for members, events and results' },
              { t:'Admin load falling on the same two volunteers every time' },
            ].map((p,i)=>(
              <div key={i} className="on-scroll" style={{
                display:'flex', alignItems:'flex-start', gap:12,
                padding:'13px 16px', borderRadius:12,
                background:'#fff', border:`1px solid rgba(0,109,119,.07)`,
                animationDelay:`${i*.06}s`,
              }}>
                <span style={{color:'#E05252',fontWeight:800,fontSize:'.95rem',flexShrink:0,marginTop:1}}>✕</span>
                <span style={{color:C.text,fontSize:'.92rem',lineHeight:1.55}}>{p.t}</span>
              </div>
            ))}
          </div>
        </div>
      </Sec>

      <ProductShowcase/>

      {/* ── FEATURES ────────────────────────────────── */}
      <Sec bg={`linear-gradient(175deg, #FBFEFC 0%, #EAF5F6 45%, #F1EDFB 100%)`} id="features" topAccent="linear-gradient(90deg,#006D77 0%,#0369A1 33%,#7C3AED 66%,#C2410C 100%)">
        {/* Subtle ambient colour blobs — this was a flat white section (still is a very light
            tint as of Session 191, not saturated colour) with most of its colour locked inside
            the 46px icon squares, sitting right after the dark Product Showcase and right before
            another pale section (Who it's for) — Ram flagged the run of white/pale sections reads
            as one long blank stretch when scrolling. These are the
            same soft-blurred-orb treatment already used in the dark sections (Hero/Showcase),
            just at much lower opacity so they read as gentle atmosphere behind the card grid
            rather than competing with the (still white) cards themselves. */}
        <div style={{
          position:'absolute', left:'4%', top:'6%', width:340, height:340, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(3,105,161,.07) 0%, transparent 70%)', pointerEvents:'none',
        }}/>
        <div style={{
          position:'absolute', right:'2%', top:'42%', width:300, height:300, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(124,58,237,.06) 0%, transparent 70%)', pointerEvents:'none',
        }}/>
        <div style={{
          position:'absolute', left:'30%', bottom:'-4%', width:280, height:280, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(194,65,12,.06) 0%, transparent 70%)', pointerEvents:'none',
        }}/>
        <div style={{textAlign:'center',marginBottom:52, position:'relative'}} className="on-scroll">
          <div className="tag tag-teal" style={{marginBottom:14}}>What Lapistics does</div>
          <h2 style={{fontWeight:800,fontSize:'clamp(1.8rem,2.8vw,2.4rem)',
            lineHeight:1.12,letterSpacing:'-.025em',color:C.navy}}>
            Everything a swim club needs.<br/>Nothing it doesn't.
          </h2>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:20}}>
          {features.map(({bg,accent,icon,label,desc},i)=>(
            <div key={i} className="card on-scroll" style={{
              padding:'24px 22px',animationDelay:`${i*.05}s`,
              borderTop:`3px solid ${accent}`,
              // was a plain white shell for every card, with colour living only inside the
              // 46px icon square — this ties each card's own accent to its shell so the
              // colour reads at a glance, not just up close.
            }}>
              <div className="feature-icon" style={{
                background:bg, marginBottom:14,
                boxShadow:`0 0 0 1px ${accent}22, 0 6px 16px ${accent}26`,
              }}>{icon}</div>
              <div style={{fontWeight:700,fontSize:'.97rem',color:C.navy,marginBottom:7}}>{label}</div>
              <div style={{color:C.grey,fontSize:'.88rem',lineHeight:1.65}}>{desc}</div>
            </div>
          ))}
        </div>

        {/* Demo nudge */}
        <div className="on-scroll" style={{
          marginTop:44, padding:'22px 28px', borderRadius:18,
          background:`linear-gradient(135deg, ${C.navy} 0%, ${C.teal} 100%)`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          flexWrap:'wrap', gap:16,
        }}>
          <div>
            <div style={{fontWeight:800,fontSize:'1rem',color:C.cream,marginBottom:4}}>
              See all of this working — right now
            </div>
            <div style={{color:'rgba(255,248,237,.55)',fontSize:'.86rem'}}>
              Explore a fully loaded demo club — members, events, results and parent views, all live.
            </div>
          </div>
          <button className="btn btn-gold" onClick={()=>nav('/demo')} style={{flexShrink:0}}>
            Explore Live Demo →
          </button>
        </div>
      </Sec>

      {/* ── WHO IT'S FOR ────────────────────────────── */}
      <Sec bg={`linear-gradient(120deg, ${C.sand} 0%, #E9F3FB 30%, #F3EAFE 55%, #FDECE0 78%, #EAF6EE 100%)`} id="who" pad="80px 0 88px" topAccent="linear-gradient(90deg,#0369A1 0%,#7C3AED 33%,#C2410C 66%,#15803D 100%)">
        <div style={{textAlign:'center',marginBottom:48}} className="on-scroll">
          <div className="tag tag-teal" style={{marginBottom:14}}>Who it's for</div>
          <h2 style={{fontWeight:800,fontSize:'clamp(1.8rem,2.8vw,2.4rem)',
            lineHeight:1.12,letterSpacing:'-.025em',color:C.navy}}>
            Every role. One platform.
          </h2>
          <p style={{color:C.grey,fontSize:'.95rem',marginTop:10,maxWidth:460,margin:'10px auto 0'}}>
            Lapistics works for everyone at the club — not just the admin who sets it up.
          </p>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(232px,1fr))',gap:18}} className="g4">
          {audiences.map(({emoji,title,desc,tint},i)=>(
            <div key={i} className="card on-scroll" style={{
              padding:'28px 22px 24px', textAlign:'center',
              animationDelay:`${i*.06}s`,
              // Each card now carries its OWN tint, matched to the same RACE_TYPE_STYLE
              // family used across the app and the showcase above (blue/purple/orange/
              // green) instead of one flat teal wash for all four — the row reads as four
              // distinct roles, not four copies of the same card with different text.
              background:`linear-gradient(160deg,#fff 55%,${tint} 100%)`,
            }}>
              <div style={{fontSize:'2rem',marginBottom:12}}>{emoji}</div>
              <div style={{fontWeight:700,fontSize:'.95rem',color:C.navy,marginBottom:7}}>{title}</div>
              <div style={{color:C.grey,fontSize:'.85rem',lineHeight:1.65}}>{desc}</div>
            </div>
          ))}
        </div>
      </Sec>

      {/* ── OUR STORY ───────────────────────────────── */}
      <section style={{
        background:`linear-gradient(150deg, ${C.navy} 0%, ${C.navyMid} 55%, ${C.teal} 100%)`,
        padding:'80px 28px', position:'relative', overflow:'hidden',
      }}>
        <LaneBg/>
        <div style={{
          position:'absolute', left:'-40px', top:'-40px',
          width:260, height:260, borderRadius:'50%',
          background:'rgba(232,197,71,.05)', pointerEvents:'none',
        }}/>
        <Wrap style={{position:'relative',zIndex:1,textAlign:'center',maxWidth:720}}>
          <div className="tag tag-gold on-scroll" style={{marginBottom:16}}>Our story</div>
          <h2 className="on-scroll" style={{
            fontWeight:900, fontSize:'clamp(1.8rem,3.2vw,2.7rem)',
            color:C.cream, lineHeight:1.1, letterSpacing:'-.03em', marginBottom:22,
          }}>
            Built in Australia,<br/>for Australian clubs
          </h2>
          <p className="on-scroll" style={{color:'rgba(255,248,237,.72)',lineHeight:1.8,fontSize:'1.02rem',marginBottom:16}}>
            Lapistics started on a race day in Western Sydney. While swimmers were in the water,
            a volunteer was working through a stack of handwritten entry cards — manually
            transcribing times into a shared document, race by race. No system. No history.
            Just whoever showed up with enough time to type.
          </p>
          <p className="on-scroll" style={{color:'rgba(255,248,237,.72)',lineHeight:1.8,fontSize:'1.02rem',marginBottom:16}}>
            Parents were asking for times nobody had yet. Results sat in a document only
            one person knew how to find.
          </p>
          <p className="on-scroll" style={{color:'rgba(255,248,237,.5)',lineHeight:1.75,fontSize:'.92rem'}}>
            We built Lapistics because Australian swim clubs deserve better than that —
            not a generic platform retrofitted for swimming, but something built from the pool up.
          </p>
        </Wrap>
      </section>

      {/* ── ROADMAP / STATUS ────────────────────────── */}
      <Sec bg={`linear-gradient(175deg, #FBFDFC 0%, #F2F7EE 45%, #FCF6E8 100%)`} id="status" topAccent={`linear-gradient(90deg, ${C.teal} 0%, ${C.gold} 100%)`}>
        <div style={{
          position:'absolute', right:'6%', top:'10%', width:320, height:320, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(0,109,119,.06) 0%, transparent 70%)', pointerEvents:'none',
        }}/>
        <div style={{
          position:'absolute', left:'8%', bottom:'4%', width:260, height:260, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(232,197,71,.08) 0%, transparent 70%)', pointerEvents:'none',
        }}/>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:56,alignItems:'start', position:'relative'}} className="g2">
          <div className="on-scroll">
            <div className="tag tag-teal" style={{marginBottom:14}}>Current status</div>
            <h2 style={{fontWeight:800,fontSize:'clamp(1.8rem,2.8vw,2.4rem)',
              lineHeight:1.12,letterSpacing:'-.025em',color:C.navy,marginBottom:18}}>
              Beta — live<br/>and getting better
            </h2>
            <p style={{color:C.grey,lineHeight:1.78,fontSize:'.97rem',marginBottom:14}}>
              Lapistics is fully working and live. We're onboarding a small number of clubs
              gradually — register your interest and you'll get early access and a direct
              say in what we build next.
            </p>
            <p style={{color:C.grey,lineHeight:1.78,fontSize:'.97rem',marginBottom:30}}>
              Early clubs help shape the roadmap and lock in founder pricing, agreed with you
              directly before anything is charged. Lapistics is founder-led, so support is by
              email, with replies within a couple of business days. Your club's data is always
              yours and always exportable.
            </p>
            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
              <button className="btn btn-gold" onClick={()=>nav('/demo')} style={{fontSize:'.88rem',padding:'11px 22px'}}>
                🏊 &nbsp;Try the demo
              </button>
              <button className="btn btn-ghost-dk" onClick={()=>nav('/get-started')} style={{fontSize:'.88rem',padding:'11px 22px'}}>
                Get early access →
              </button>
            </div>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:6}} className="on-scroll">
            {roadmap.map(({done,label},i)=>(
              <div key={i} className="rm-row" style={{
                background: done ? C.white : `rgba(0,109,119,.03)`,
                border: done ? '1px solid rgba(0,109,119,.09)' : '1px dashed rgba(0,109,119,.15)',
                animationDelay:`${i*.04}s`,
              }}>
                <div style={{
                  width:24, height:24, borderRadius:'50%', flexShrink:0,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'.7rem', fontWeight:800,
                  background: done ? C.teal : '#d4e0e2',
                  color: done ? '#fff' : '#94a3b8',
                }}>
                  {done ? '✓' : '○'}
                </div>
                <span style={{
                  fontSize:'.9rem', fontWeight: done ? 600 : 400,
                  color: done ? C.text : '#94a3b8', flex:1,
                }}>
                  {label}
                </span>
                {!done && (
                  <span style={{fontSize:'.62rem',fontWeight:700,color:C.pool,
                    letterSpacing:'.1em',textTransform:'uppercase'}}>Soon</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </Sec>

      {/* ── FINAL CTA ───────────────────────────────── */}
      <section style={{
        background:`linear-gradient(135deg, ${C.teal} 0%, ${C.tealBr} 100%)`,
        padding:'80px 28px', textAlign:'center', position:'relative', overflow:'hidden',
      }}>
        <LaneBg/>
        <Wrap style={{position:'relative',zIndex:1,maxWidth:580}}>
          <div className="on-scroll" style={{display:'flex',justifyContent:'center',marginBottom:18}}>
            <div className="logo-pill">
              <img src="/assets/lapistics_logo_full.png" srcSet="/assets/lapistics_logo_full@2x.png 2x"
                alt="Lapistics™" style={{height:66,width:'auto',display:'block'}}/>
            </div>
          </div>
          <h2 className="on-scroll" style={{
            fontWeight:900, fontSize:'clamp(1.8rem,3.5vw,2.7rem)',
            color:C.cream, lineHeight:1.1, letterSpacing:'-.03em', marginBottom:14,
          }}>
            Your club deserves better<br/>than a spreadsheet.
          </h2>
          <p className="on-scroll" style={{
            color:'rgba(255,248,237,.74)', fontSize:'1rem',
            lineHeight:1.74, marginBottom:34,
          }}>
            Lapistics gives your club a proper home for members, events, results and parent
            communication — purpose-built for Australian swimming. Founder pricing for early clubs. No lock-in.
          </p>
          <div className="on-scroll cta-row" style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
            <button className="btn btn-white" onClick={()=>nav('/demo')}>🏊 &nbsp;Explore Live Demo</button>
            <button className="btn btn-outline" onClick={()=>nav('/get-started')}>Register your interest →</button>
          </div>
          <div className="on-scroll" style={{marginTop:14,fontSize:'.74rem',color:'rgba(255,248,237,.36)'}}>
            No account needed for demo · No lock-in
          </div>
        </Wrap>
      </section>

      {/* ── FOOTER ──────────────────────────────────── */}
      <footer style={{background:C.navy, padding:'28px 28px'}}>
        <Wrap style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:14}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div className="logo-pill" style={{padding:'4px 12px',borderRadius:8}}>
              <img src="/assets/lapistics_logo_full.png" srcSet="/assets/lapistics_logo_full@2x.png 2x"
                alt="Lapistics™" style={{height:40,width:'auto',display:'block'}}/>
            </div>
            <span style={{color:'rgba(255,248,237,.4)',fontSize:'.76rem'}}>
              © 2026 Lapistics. All rights reserved.
            </span>
          </div>
          <div style={{display:'flex',gap:20,alignItems:'center',flexWrap:'wrap'}}>
            {[
              ['mailto:hello@lapistics.com.au','hello@lapistics.com.au'],
              ['/privacy-policy','Privacy Policy'],
              ['/terms','Terms of Service'],
            ].map(([h,l])=>(
              <a key={l} href={h} style={{color:'rgba(255,248,237,.32)',fontSize:'.78rem',textDecoration:'none',transition:'color .15s'}}
                onMouseOver={e=>e.currentTarget.style.color=C.gold}
                onMouseOut={e=>e.currentTarget.style.color='rgba(255,248,237,.32)'}>
                {l}
              </a>
            ))}
            <button onClick={()=>nav('/login')} style={{
              background:'none',border:'none',cursor:'pointer',padding:0,
              color:'rgba(255,248,237,.32)',fontSize:'.78rem',transition:'color .15s',
            }}
            onMouseOver={e=>e.currentTarget.style.color=C.gold}
            onMouseOut={e=>e.currentTarget.style.color='rgba(255,248,237,.32)'}>
              Sign in
            </button>
          </div>
        </Wrap>
      </footer>
    </>
  );
}
